import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useMedia } from '@/hooks/useMedia';
import { useAnimeMedia } from '@/hooks/useAnimeMedia';
import { useWatchHistory } from '@/hooks/useWatchHistory';
import { useConstant } from '@/hooks/useConstant';
import { useSubtitlePref } from '@/hooks/useSubtitlePref';
import { useLobbyPlayerSync } from '@/hooks/useLobbyPlayerSync';
import { useHlsPlayer } from '@/hooks/useHlsPlayer';
import { useSourceFailover } from '@/hooks/useSourceFailover';
import { useNextEpisodePrefetch } from '@/hooks/useNextEpisodePrefetch';
import { buildPlaylistUrl, getTracks, prefetchStream } from '@/services/streamingApi';
import { fetchSubtitleAsBlobUrl } from '@/services/subtitlesApi';
import { ApiClientError } from '@/services/apiClient';
import { isKnownSourceName, type SourceName, type StreamRef, type TrackEntry, type TvDetails } from '@/types/streaming';
import { pickAlternates } from '@/lib/streamingAlternates';
import type { RecordWatchInput } from '@/services/historyApi';
import { useAppSelector } from '@/store';
import { Preroll } from '@/components/Preroll';
import { shouldShowPreroll } from '@/lib/ads/shouldShowPreroll';
import { adTagUrl } from '@/lib/ads/ima';
import { LobbyChat } from '@/components/LobbyChat';
import { SourceMenu } from '@/components/SourceMenu';
import { ResumePrompt } from '@/components/ResumePrompt';
import { EpisodePicker } from '@/components/EpisodePicker';
import { TvEpisodeControls } from '@/components/TvEpisodeControls';
import Hls from 'hls.js';
import { SubtitlePicker, type ActiveSelection, type NativeSubTrack } from '@/components/SubtitlePicker';
import { normalizeSubtitleLang } from '@/lib/subtitleLang';
import { BufferControl, useExtraBufferPref } from '@/components/BufferControl';
import { P2pBadge } from '@/components/P2pBadge';
import { CustomPlayer } from '@/components/CustomPlayer/CustomPlayer';
import { QualityPanel } from '@/components/CustomPlayer/QualityPanel';
import { useHlsQuality } from '@/components/CustomPlayer/useHlsQuality';
import { useStreamDownload } from '@/components/CustomPlayer/useStreamDownload';
import type { P2pMode } from '@/lib/p2pConfig';

interface Props {
  streamRef: StreamRef;
}

// Build a navigate target for a TV/anime ref. The picker calls navigate()
// instead of mutating local state so the URL bar always reflects the playing
// episode — that is what makes a reload land on the same episode and what
// keeps the lobby `now_playing` server-state in sync with the visible URL.
function watchPathFor(ref: StreamRef, source: SourceName | null): string {
  const base = ref.type === 'movie'
    ? `/watch/movie/${ref.tmdbId}`
    : ref.type === 'tv'
    ? `/watch/tv/${ref.tmdbId}/${ref.season}/${ref.episode}`
    : `/watch/anime/${ref.malId}/${ref.episode}/${ref.audio}`;
  return source ? `${base}?source=${encodeURIComponent(source)}` : base;
}

function refKey(ref: StreamRef, source: SourceName | null): string {
  const base = ref.type === 'movie'
    ? `movie:${ref.tmdbId}`
    : ref.type === 'tv'
    ? `tv:${ref.tmdbId}:${ref.season}:${ref.episode}`
    : `anime:${ref.malId}:${ref.episode}:${ref.audio}`;
  return `${base}|${source ?? 'auto'}`;
}

function tvLabel(ref: StreamRef, t: TFunction): string {
  if (ref.type === 'tv') {
    const s = String(ref.season).padStart(2, '0');
    const e = String(ref.episode).padStart(2, '0');
    return t('watch.tvSuffix', { season: s, episode: e });
  }
  if (ref.type === 'anime') {
    const e = String(ref.episode).padStart(2, '0');
    const tag = ref.audio === 'dub' ? 'DUB' : 'SUB';
    return t('watch.animeSuffix', { episode: e, tag });
  }
  return '';
}

const RECORD_THROTTLE_MS = 10_000;
const MIN_PROGRESS_TO_RECORD_S = 5;
const NEAR_COMPLETE_FRACTION = 0.95;
/**
 * Suppress lobby play/pause/seek broadcasts for this long around a silent
 * failover swap. Covers: hls.loadSource → manifest fetch (~0.5–1.5s) →
 * MANIFEST_PARSED → restore currentTime + play() → first FRAG_LOADED (~1–3s).
 * Picked to comfortably cover slow manifest fetches without eating genuine
 * user pause/seek for so long that the room stays out of sync.
 */
const FAILOVER_SWAP_SUPPRESS_MS = 8_000;

export function Watch({ streamRef }: Props): JSX.Element {
  // URL is the single source of truth for which episode is playing. The
  // picker components call navigate() to change it; React Router re-renders
  // this component with a new `streamRef` prop and the player re-keys via
  // `useHlsPlayer`'s `key` dep. An earlier version lifted `streamRef` into
  // local state so the picker could swap episodes without touching the URL,
  // but that silently dropped legitimate prop changes too (an `<EpisodeTile>`
  // click navigates to a new episode within the same series — same `seriesKey`
  // — and the lifted state ignored the new prop, leaving the user on the old
  // episode). It also desynced lobby `now_playing` from the URL bar, so a
  // reload re-prompted "Join the watch party?" even though the user was
  // already on the lobby's pick.
  const navigate = useNavigate();
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [tracks, setTracks] = useState<TrackEntry[]>([]);
  const [retryToken, setRetryToken] = useState(0);

  const [searchParams, setSearchParams] = useSearchParams();
  const rawSource = searchParams.get('source');
  const activeSource: SourceName | null = isKnownSourceName(rawSource) ? rawSource : null;

  // useMedia is TMDB-keyed; for anime refs we use useAnimeMedia instead. To
  // keep the hook call order stable across renders we still invoke useMedia
  // with sentinel values for anime — its fetch hits /movie/0 which 404s and
  // is never rendered (the title block below picks animeDetails for anime).
  const useMediaArgs = streamRef.type === 'anime'
    ? { type: 'movie' as const, tmdbId: 0 }
    : { type: streamRef.type, tmdbId: streamRef.tmdbId };
  const { details, episodes, loading, error, loadSeason } = useMedia(useMediaArgs);
  const animeMalId = streamRef.type === 'anime' ? streamRef.malId : null;
  const { details: animeDetails, loading: animeLoading, error: animeError } = useAnimeMedia(animeMalId);
  const inLobby = useAppSelector((s) => s.lobby.currentLobby != null);
  const { entries, recordWatch } = useWatchHistory();
  const failoverBufferSeconds = useConstant('failoverBufferThresholdSeconds');
  const failoverSegment4xx = useConstant('failoverSegment4xxThreshold');
  const failoverWindowMs = useConstant('failoverErrorWindowMs');
  const nextEpisodeLeadSeconds = useConstant('nextEpisodeLeadSeconds');
  const { lang: prefLang, setLang: setPrefLang } = useSubtitlePref();
  const [subtitleSelection, setSubtitleSelection] = useState<ActiveSelection>({ kind: 'off' });
  // Cached blob URL for the currently-mounted OpenSubtitles track. Revoked
  // when replaced or on unmount so we don't leak Blob backings.
  const osBlobUrlRef = useRef<string | null>(null);
  const [osBlobTrack, setOsBlobTrack] = useState<{ lang: string; url: string } | null>(null);
  // Why the last external-subtitle fetch failed (shown inside the picker).
  const [subtitleFileError, setSubtitleFileError] = useState<'quota' | 'generic' | null>(null);
  // In-manifest subtitle renditions (VixSrc et al. ship these), surfaced in
  // the picker and controlled EXCLUSIVELY through the hls.js API below.
  const [nativeSubTracks, setNativeSubTracks] = useState<NativeSubTrack[]>([]);

  const key = refKey(streamRef, activeSource);

  // Last known playhead, used to resume in place across a hard reload (the
  // manual Retry button). Continuously updated from `timeupdate` below so any
  // reload of the SAME title/source resumes where the viewer was rather than
  // restarting at 0. Reset to 0 during render whenever `key` changes (a new
  // title / episode / source is a fresh start — the ResumePrompt owns resume
  // there, not this hard-reload path). Render-time reset (not an effect) so
  // `useHlsPlayer` reads 0 on the very render that switches titles.
  const resumePositionRef = useRef(0);
  const prevKeyRef = useRef(key);
  if (prevKeyRef.current !== key) {
    prevKeyRef.current = key;
    resumePositionRef.current = 0;
  }

  function retryStream(): void {
    // Preserve the playhead so Retry resumes from where the viewer was instead
    // of restarting the title at 0.
    const ct = videoRef.current?.currentTime;
    if (typeof ct === 'number' && Number.isFinite(ct) && ct > 0) {
      resumePositionRef.current = ct;
    }
    setRetryToken((n) => n + 1);
  }

  // History recording is suppressed while the resume prompt is up so the
  // throttled timeupdate writes don't overwrite the saved progress before the
  // user chooses Resume / Start over.
  const resumeOpenRef = useRef(true);
  const lastRecordAtRef = useRef(0);

  // Reset the resume gate whenever the URL changes — a new episode means a
  // fresh chance to show the prompt.
  useEffect(() => {
    resumeOpenRef.current = true;
    lastRecordAtRef.current = 0;
  }, [key]);

  const { value: extraBuffer, setValue: setExtraBuffer } = useExtraBufferPref();

  // P2P swarm membership for VOD: only when the user is in a lobby with the
  // same ref. Solo viewers skip the swarm because (a) they probably don't
  // benefit (different timestamps), (b) it would expose their IP to strangers,
  // and (c) it costs upload bandwidth without payoff. Lobby members opt-in
  // implicitly by joining a watch-together feature.
  const lobbyId = useAppSelector((s) => s.lobby.currentLobby?.id ?? null);
  const lobbyNowPlaying = useAppSelector((s) => s.lobby.nowPlaying);

  // Billing-gated UI. Reads the snapshot BillingGate already fetches on watch
  // routes (no extra fetch here). Capability checks default permissive while the
  // paywall is off or billing hasn't loaded, so nothing changes until enforced.
  const billing = useAppSelector((s) => s.billing.data);
  const canDownload = !billing?.paywallEnabled || (billing?.capabilities.includes('downloads') ?? false);
  const [adDone, setAdDone] = useState(false);
  const needAd = !adDone && shouldShowPreroll(billing, Boolean(adTagUrl()));
  const p2pMode: P2pMode | null = useMemo(() => {
    if (!lobbyId) return null;
    // Only swarm with peers watching the EXACT same ref + source. A mismatch
    // would just churn (no shared segments) and confuse the badge stats.
    const currentRefKey = `${streamRef.type}:${
      streamRef.type === 'movie' ? streamRef.tmdbId
      : streamRef.type === 'tv' ? `${streamRef.tmdbId}.${streamRef.season}.${streamRef.episode}`
      : `${streamRef.malId}.${streamRef.episode}.${streamRef.audio}`}|${activeSource ?? 'auto'}`;
    if (lobbyNowPlaying) {
      // Watch.tsx only handles movie/tv/anime streams — sports-vod lives on
      // /watch/sports-vod/:id with its own player. A sports-vod lobby ref here
      // means the user is on a Watch page that doesn't match the lobby pick,
      // so refuse to join the swarm rather than emit a mismatched key.
      const r = lobbyNowPlaying.ref;
      if (r.kind === 'sports-vod') return null;
      const lobbyRefKey = `${r.kind}:${
        r.kind === 'movie' ? r.tmdbId
        : r.kind === 'tv' ? `${r.tmdbId}.${r.season}.${r.episode}`
        : `${r.malId}.${r.episode}.${r.audio}`}|${lobbyNowPlaying.source ?? 'auto'}`;
      if (lobbyRefKey !== currentRefKey) return null;
    }
    return { kind: 'lobby', lobbyId, refKey: currentRefKey };
  }, [lobbyId, lobbyNowPlaying, streamRef, activeSource]);

  // Opt the playlist request into the lobby's SHARED backend session ONLY when
  // we're actually swarming (same lobby + same ref) — keeps the backend's
  // shared-session scope aligned 1:1 with the P2P swarm scope. Solo viewers
  // (and lobby members watching something other than the lobby's pick) get a
  // private per-viewer session.
  const sharedLobbyId = p2pMode?.kind === 'lobby' ? p2pMode.lobbyId : undefined;

  const { fatal, hls, p2pStats } = useHlsPlayer({
    playlistUrl: buildPlaylistUrl(streamRef, activeSource ?? undefined, sharedLobbyId),
    videoRef,
    retryToken,
    key,
    extraBuffer,
    p2pMode,
    // VOD: auto-recover transient network fatals (e.g. a 502 on a segment) in
    // place, resuming from the playhead, before surfacing the overlay.
    autoRecoverNetwork: true,
    resumePosition: resumePositionRef.current,
  });

  // Quality selection + client-side MP4 download. Both ride the live hls.js
  // instance, so they light up only on the hls.js VOD path (hls != null). The
  // download grabs the currently-selected rendition (Auto → highest).
  const mediaTitle = streamRef.type === 'anime'
    ? (animeDetails?.title ?? undefined)
    : (details?.title ?? undefined);
  const quality = useHlsQuality(hls);
  const download = useStreamDownload(hls, {
    title: mediaTitle,
    getDownloadLevel: () => quality.current,
  });

  const failoverAlternates = useMemo(
    () => pickAlternates(streamRef, activeSource),
    [streamRef, activeSource],
  );

  // Lobby sync first so its `suppress` handle is available to the failover
  // hook below. The handle is stable across renders (memoised in the hook).
  const lobbySync = useLobbyPlayerSync({ videoRef, streamRef, activeSource });

  useSourceFailover({
    hls,
    videoRef,
    currentSource: activeSource,
    alternates: failoverAlternates,
    buildUrl: (src) => buildPlaylistUrl(streamRef, src as SourceName, sharedLobbyId),
    prefetch: (src) => prefetchStream(streamRef, src as SourceName),
    mediaKind: 'vod',
    thresholds: {
      bufferSeconds: failoverBufferSeconds,
      segment4xx: failoverSegment4xx,
      windowMs: failoverWindowMs,
    },
    // Wrap the swap window in a lobby-suppress so the synthetic pause/seek/
    // play events hls.js emits while reloading the manifest don't broadcast
    // to peers as if the viewer initiated them. Without this, every silent
    // failover yanked the rest of the room around for ~1s.
    onSwap: () => lobbySync.suppress(FAILOVER_SWAP_SUPPRESS_MS),
  });

  // TV-only: warm the NEXT episode's backend resolve as the viewer nears the
  // end of the current one, so clicking Next only pays for the playlist GET
  // rather than the full provider-chain walk. No-op for movies/anime.
  useNextEpisodePrefetch({
    videoRef,
    streamRef,
    details: streamRef.type === 'tv' ? (details as TvDetails | null) : null,
    activeSource,
    leadSeconds: nextEpisodeLeadSeconds,
  });

  useEffect(() => {
    let cancelled = false;
    getTracks(streamRef, activeSource ?? undefined)
      .then((t) => { if (!cancelled) setTracks(t); })
      .catch(() => { if (!cancelled) setTracks([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Throttled history recording. Skips the first 5s and anything past 95% so
  // we don't pollute continue-watching with accidental clicks or finished
  // playthroughs.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    function buildInput(): RecordWatchInput | null {
      if (!video) return null;
      const ct = video.currentTime;
      const dur = video.duration;
      if (!Number.isFinite(ct) || ct < MIN_PROGRESS_TO_RECORD_S) return null;
      if (Number.isFinite(dur) && dur > 0 && ct / dur > NEAR_COMPLETE_FRACTION) return null;
      const progressMs = Math.floor(ct * 1000);
      if (streamRef.type === 'movie') {
        return { movieId: streamRef.tmdbId, progress: progressMs };
      }
      if (streamRef.type === 'anime') {
        return {
          animeMalId: streamRef.malId,
          episodeNumber: streamRef.episode,
          animeAudio: streamRef.audio,
          progress: progressMs,
        };
      }
      return {
        tvId: streamRef.tmdbId,
        seasonNumber: streamRef.season,
        episodeNumber: streamRef.episode,
        progress: progressMs,
      };
    }

    function maybeRecord(force = false): void {
      if (resumeOpenRef.current) return;
      const now = Date.now();
      if (!force && now - lastRecordAtRef.current < RECORD_THROTTLE_MS) return;
      const input = buildInput();
      if (!input) return;
      lastRecordAtRef.current = now;
      recordWatch(input);
    }

    function onTimeUpdate(): void {
      // Track the live playhead so a hard reload (Retry / buffer toggle) can
      // resume here instead of restarting at 0.
      const ct = video?.currentTime;
      if (typeof ct === 'number' && Number.isFinite(ct) && ct > 0) {
        resumePositionRef.current = ct;
      }
      maybeRecord(false);
    }
    function onPause(): void { maybeRecord(true); }

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('pause', onPause);
    return (): void => {
      // Final flush on unmount (e.g. navigating to the next episode).
      maybeRecord(true);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('pause', onPause);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, recordWatch, streamRef]);

  function handleSelect(next: SourceName | null): void {
    const params = new URLSearchParams(searchParams);
    if (next) params.set('source', next);
    else params.delete('source');
    setSearchParams(params, { replace: false });
  }

  // Hydrate the active selection from the persisted pref once tracks come
  // in — provider tracks first, then in-manifest renditions (both are
  // zero-cost; auto-fetching OpenSubtitles on every play stays out of scope).
  useEffect(() => {
    if (prefLang === 'off') return;
    if (subtitleSelection.kind !== 'off') return;
    const wanted = prefLang.toLowerCase();
    const match = tracks.find((t) => t.lang.toLowerCase() === wanted);
    if (match) {
      setSubtitleSelection({ kind: 'provider', lang: match.lang, url: match.url });
      return;
    }
    const native = nativeSubTracks.find((t) => t.lang === normalizeSubtitleLang(wanted));
    if (native) {
      setSubtitleSelection({ kind: 'hls', lang: native.lang, id: native.id });
    }
  }, [prefLang, tracks, nativeSubTracks, subtitleSelection.kind]);

  // Read the manifest's subtitle renditions off the live hls.js instance and
  // keep them out of hls.js's own hands until the user picks one: some
  // manifests mark a rendition DEFAULT/FORCED and hls.js would light it up
  // unasked — a reported "subtitles are wrong" source (forced Italian etc.).
  useEffect(() => {
    if (!hls) { setNativeSubTracks([]); return undefined; }
    const read = (): void => {
      setNativeSubTracks((hls.subtitleTracks ?? []).map((t, i) => ({
        id: typeof t.id === 'number' ? t.id : i,
        lang: normalizeSubtitleLang(t.lang),
        label: t.name || t.lang || `Track ${i + 1}`,
      })));
    };
    hls.subtitleDisplay = false;
    hls.subtitleTrack = -1;
    read();
    hls.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, read);
    return (): void => { hls.off(Hls.Events.SUBTITLE_TRACKS_UPDATED, read); };
  }, [hls]);

  // Drive the hls.js side of the selection. Native renditions MUST go through
  // hls.subtitleTrack — hls.js's subtitle controller listens for raw
  // textTrack mode changes and re-disables everything it manages, so flipping
  // modes by hand loses that race (the "picked a subtitle, nothing showed"
  // defect on manifests with embedded renditions).
  useEffect(() => {
    if (!hls) return;
    if (subtitleSelection.kind === 'hls') {
      hls.subtitleDisplay = true;
      hls.subtitleTrack = subtitleSelection.id;
    } else {
      hls.subtitleDisplay = false;
      if (hls.subtitleTrack !== -1) hls.subtitleTrack = -1;
    }
  }, [hls, subtitleSelection]);

  // Reset selection on stream change so a lang that doesn't exist on the new
  // episode doesn't carry over and silently fail.
  useEffect(() => {
    setSubtitleSelection({ kind: 'off' });
    setSubtitleFileError(null);
    if (osBlobUrlRef.current) {
      URL.revokeObjectURL(osBlobUrlRef.current);
      osBlobUrlRef.current = null;
    }
    setOsBlobTrack(null);
  }, [key]);

  // External-subtitle fetch: when the user picks a searched entry, fetch it
  // as a blob and stage it as an extra <track>. We only refetch when the
  // fileId actually changes. When the selection moves AWAY from external
  // (provider pick or Off) the blob must be revoked and its <track>
  // unmounted — only the selected track may stay mounted (see mountedTracks).
  useEffect(() => {
    if (subtitleSelection.kind !== 'external') {
      if (osBlobUrlRef.current) {
        URL.revokeObjectURL(osBlobUrlRef.current);
        osBlobUrlRef.current = null;
      }
      setOsBlobTrack(null);
      return undefined;
    }
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const url = await fetchSubtitleAsBlobUrl(subtitleSelection.provider, subtitleSelection.fileId);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        if (osBlobUrlRef.current) URL.revokeObjectURL(osBlobUrlRef.current);
        osBlobUrlRef.current = url;
        setOsBlobTrack({ lang: subtitleSelection.lang, url });
      } catch (err) {
        if (cancelled) return;
        // Reset to off but keep the persisted pref so the next episode can
        // try again automatically (per spec). Surface WHY it failed — a
        // silent revert to Off is indistinguishable from a dead click.
        setSubtitleFileError(
          err instanceof ApiClientError && err.status === 429 ? 'quota' : 'generic',
        );
        setSubtitleSelection({ kind: 'off' });
        if (osBlobUrlRef.current) {
          URL.revokeObjectURL(osBlobUrlRef.current);
          osBlobUrlRef.current = null;
        }
        setOsBlobTrack(null);
      }
    })();
    return (): void => { cancelled = true; };
  }, [subtitleSelection]);

  // Apply textTracks mode whenever the active selection or the visible tracks
  // change — this is how the browser shows/hides a specific <track>.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    // Match on the track's id (the <track> element carries its URL as id) —
    // NOT on language: providers routinely ship several tracks of the same
    // lang ("English", "English 2") and the OpenSubtitles blob track can
    // share a lang with a provider track, which would stack duplicate cue
    // renderings if all of them went to 'showing'.
    const targetId = subtitleSelection.kind === 'provider'
      ? subtitleSelection.url
      : subtitleSelection.kind === 'external'
      ? osBlobTrack?.url ?? null
      : null;
    const apply = (): void => {
      const ttracks = video.textTracks;
      for (let i = 0; i < ttracks.length; i += 1) {
        const tt = ttracks[i];
        // Tracks WITHOUT an id are hls.js-created (in-manifest renditions)
        // and belong to the hls selection path above — leave them to hls.js
        // when a native rendition is active, park them DISABLED otherwise.
        // 'hidden' would read as "user enabled a subtitle" to hls.js's
        // change listener, which then re-disables everything.
        if (!tt.id) {
          if (subtitleSelection.kind !== 'hls') tt.mode = 'disabled';
          continue;
        }
        if (subtitleSelection.kind === 'hls') { tt.mode = 'disabled'; continue; }
        tt.mode = targetId != null && tt.id === targetId ? 'showing' : 'disabled';
      }
    };
    apply();
    // hls.js flips modes asynchronously around subtitleTrack switches (e.g.
    // re-showing the old native rendition AFTER we've enabled an external
    // track — observed live as two stacked subtitle rows). Re-assert on
    // every textTracks 'change' until the system converges: writing an
    // unchanged mode fires no further change events, so this terminates.
    const list = video.textTracks as TextTrackList & {
      addEventListener?: (t: string, fn: () => void) => void;
      removeEventListener?: (t: string, fn: () => void) => void;
    };
    if (typeof list.addEventListener === 'function') {
      list.addEventListener('change', apply);
      return (): void => { list.removeEventListener?.('change', apply); };
    }
    return undefined;
  }, [subtitleSelection, osBlobTrack, tracks]);

  // Revoke any blob URL on unmount so we don't leak across navigation.
  useEffect(() => {
    return (): void => {
      if (osBlobUrlRef.current) {
        URL.revokeObjectURL(osBlobUrlRef.current);
        osBlobUrlRef.current = null;
      }
    };
  }, []);

  function handleSubtitleSelect(sel: ActiveSelection): void {
    setSubtitleFileError(null);
    setSubtitleSelection(sel);
    if (sel.kind === 'off') setPrefLang('off');
    else setPrefLang(sel.lang);
  }

  const activeSubtitleLang = useMemo<string | 'off'>(
    () => (subtitleSelection.kind === 'off' ? 'off' : subtitleSelection.lang),
    [subtitleSelection],
  );

  // Mount ONLY the selected provider track. Mounting all of them (some
  // providers ship 15+) meant every HLS error recovery — which reloads the
  // media element and thereby resets + refetches every <track> — restarted
  // 15 proxied VTT downloads; on a flaky stream the cue lists never finished
  // repopulating. One small selected track reloads in well under a second,
  // and re-selection always mounts a fresh element (a <track> that failed
  // once never retries on its own).
  const mountedTracks = useMemo<TrackEntry[]>(
    () => (subtitleSelection.kind === 'provider'
      ? tracks.filter((t) => t.url === subtitleSelection.url)
      : []),
    [subtitleSelection, tracks],
  );

  const stageClass = [
    'watch-stage',
    inLobby ? 'watch-stage--with-chat' : '',
    streamRef.type === 'tv' ? 'watch-stage--with-picker' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={stageClass} data-testid="watch-stage">
      {/* Limited users past their daily free plays watch one pre-roll first.
          Inactive (renders null) for everyone else and whenever the paywall is
          off or no ad tag is configured — i.e. always, until ads go live. */}
      <Preroll active={needAd} onComplete={() => setAdDone(true)} />
      <CustomPlayer
        videoRef={videoRef}
        fatal={fatal}
        onRetry={retryStream}
        autoMuted={lobbySync.autoMuted}
        onUnmute={lobbySync.unmute}
        tracks={mountedTracks}
        osBlobTrack={osBlobTrack}
        title={mediaTitle}
        download={hls && canDownload ? { state: download.state, onStart: download.start, onCancel: download.cancel } : null}
        settingsPanels={{
          quality: quality.levels.length > 0 ? (
            <QualityPanel
              levels={quality.levels}
              current={quality.current}
              onSelect={quality.setLevel}
            />
          ) : undefined,
          source: (
            <SourceMenu
              streamRef={streamRef}
              activeSource={activeSource}
              onSelect={handleSelect}
            />
          ),
          subtitles: (
            <SubtitlePicker
              target={
                streamRef.type === 'movie' ? { type: 'movie', tmdbId: streamRef.tmdbId }
                : streamRef.type === 'tv' ? {
                  type: 'tv', tmdbId: streamRef.tmdbId, season: streamRef.season, episode: streamRef.episode,
                }
                : null
              }
              providerTracks={tracks}
              nativeTracks={nativeSubTracks}
              activeLang={activeSubtitleLang}
              fileError={subtitleFileError}
              onSelect={handleSubtitleSelect}
            />
          ),
          buffer: <BufferControl value={extraBuffer} onChange={setExtraBuffer} />,
        }}
      />

      <div className="watch-stage__title">
        {(streamRef.type === 'anime' ? animeLoading : loading) && <span>{t('common.loading')}</span>}
        {(streamRef.type === 'anime' ? animeError : error) && (
          <span>{t('watch.error', { error: streamRef.type === 'anime' ? animeError : error })}</span>
        )}
        {streamRef.type === 'anime' && animeDetails && (
          <span>
            {animeDetails.title}
            {tvLabel(streamRef, t)}
          </span>
        )}
        {streamRef.type !== 'anime' && details && (
          <span>
            {details.title}
            {tvLabel(streamRef, t)}
          </span>
        )}
      </div>

      <SourceMenu streamRef={streamRef} activeSource={activeSource} onSelect={handleSelect} />

      <BufferControl value={extraBuffer} onChange={setExtraBuffer} />

      <P2pBadge stats={p2pStats} active={p2pMode != null} />

      {details && streamRef.type === 'tv' && (
        <TvEpisodeControls
          streamRef={streamRef}
          details={details as TvDetails}
          episodes={episodes}
          loadSeason={loadSeason}
          onChange={(next) => navigate(watchPathFor(next, activeSource))}
        />
      )}

      {details && streamRef.type === 'tv' && (
        <EpisodePicker
          streamRef={streamRef}
          details={details as TvDetails}
          episodes={episodes}
          loadSeason={loadSeason}
        />
      )}

      {details && (
        <ResumePrompt
          key={key}
          videoRef={videoRef}
          streamRef={streamRef}
          entries={entries}
          onResolved={(): void => { resumeOpenRef.current = false; }}
        />
      )}

      <LobbyChat enabled={inLobby} />
    </div>
  );
}
